<script setup lang="ts">
/**
 * Export mode picker — brief 01-export.md + 03-procreate.md. Entry point for
 * all three export paths; each opens its own panel within this same sheet.
 */
import { ref, watch } from 'vue';
import { IconBrush, IconGrid4x4, IconLock, IconVector, IconX } from '@tabler/icons-vue';
import { useEntitlementStore } from '@/stores/entitlement';
import { useUiStore } from '@/stores/ui';
import PsdExportPanel from '@/components/export/PsdExportPanel.vue';
import WallpaperExportPanel from '@/components/export/WallpaperExportPanel.vue';
import VectorExportPanel from '@/components/export/VectorExportPanel.vue';

const ui = useUiStore();
const entitlement = useEntitlementStore();

type Mode = 'menu' | 'psd' | 'wallpaper' | 'vector';
const mode = ref<Mode>('menu');

// Reset to the top-level menu each time the sheet is reopened.
watch(
  () => ui.showExport,
  (open) => {
    if (open) mode.value = 'menu';
  },
);

function close(): void {
  ui.showExport = false;
}

/** Export is no longer entirely paid-only (brief 12-vector_draw) — only PSD
 *  and standalone vector export stay pro-locked; Wallpaper is free (at the
 *  sizes WallpaperExportPanel itself gates). */
function pickMode(m: 'psd' | 'vector'): void {
  if (!entitlement.canExport) {
    ui.openPaywall('export');
    return;
  }
  mode.value = m;
}
</script>

<template>
  <div v-if="ui.showExport" class="sheet-backdrop" @click.self="close">
    <div class="sheet" role="dialog" aria-modal="true" aria-label="Export">
      <header>
        <h2>Export</h2>
        <button class="icon-close" aria-label="Close" @click="close">
          <IconX :size="18" stroke-width="2" />
        </button>
      </header>

      <div v-if="mode === 'menu'" class="menu">
        <button class="mode-btn" @click="pickMode('psd')">
          <IconBrush :size="20" stroke-width="1.75" />
          <div class="mode-text">
            <strong>Export for Editing (PSD)</strong>
            <span>Paint layers + guides for Procreate/Photoshop, render it back as a wallpaper</span>
          </div>
          <IconLock v-if="!entitlement.canExport" :size="15" stroke-width="2" class="mode-lock" />
        </button>
        <button class="mode-btn" @click="mode = 'wallpaper'">
          <IconGrid4x4 :size="20" stroke-width="1.75" />
          <div class="mode-text">
            <strong>Wallpaper (Raster)</strong>
            <span>One flat PNG of the full repeating pattern — free at 2×2/3×3</span>
          </div>
        </button>
        <button class="mode-btn" @click="pickMode('vector')">
          <IconVector :size="20" stroke-width="1.75" />
          <div class="mode-text">
            <strong>Vector (SVG / DXF)</strong>
            <span>Traced shapes as cut-ready vector copies</span>
          </div>
          <IconLock v-if="!entitlement.canExport" :size="15" stroke-width="2" class="mode-lock" />
        </button>
      </div>

      <PsdExportPanel v-else-if="mode === 'psd'" @back="mode = 'menu'" @done="close" />
      <WallpaperExportPanel v-else-if="mode === 'wallpaper'" @back="mode = 'menu'" @done="close" />
      <VectorExportPanel v-else-if="mode === 'vector'" @back="mode = 'menu'" @done="close" />
    </div>
  </div>
</template>

<style scoped>
.sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 8, 16, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.sheet {
  width: min(520px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  padding: 20px;
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

h2 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary);
}

.icon-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
}

.icon-close:hover {
  color: var(--text-primary);
}

.menu {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mode-btn {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  text-align: left;
  background: var(--canvas-bg);
  border: 1px solid var(--ui-border);
  border-radius: 10px;
  color: var(--text-primary);
  cursor: pointer;
}

.mode-btn:hover {
  border-color: var(--accent);
}

.mode-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mode-text strong {
  font-size: 14px;
}

.mode-text span {
  font-size: 12px;
  color: var(--text-muted);
}

.mode-lock {
  align-self: flex-start;
  color: var(--text-muted);
  flex-shrink: 0;
}
</style>
